import React, { useEffect, useState } from "react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Banner from "../../../components/ui/Banner/Banner.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import { useApp } from "../../../hooks/useApp.js";
import { useTotals } from "../../../hooks/useTotals.js";
import { money } from "../../../utils/format.js";
import { getToken } from "../../../services/api.js";
import { getReturnForBusiness } from "../../../services/trucksService.js";
import { getPayment, savePayment } from "../../../services/paymentService.js";
import { getPaymentMethods } from "../../../services/lookupService.js";

// Matches the current backend seed data (lookup_payment_methods). Kept as
// the initial state so the step still renders immediately if the lookup
// fetch is slow or fails. Check/money-order was removed - it has no
// representation anywhere in the IRS e-file schema (confirmed absent from
// IRS2290.xsd and the Business Rules CSV), so a balance-due return could
// never be validly e-filed with it as the chosen method.
const DEFAULT_METHODS = [
  { id: "efw", title: "EFW", sub: "We debit the tax when the IRS accepts the return. Fastest." },
  { id: "eftps", title: "EFTPS", sub: "You pay separately at EFTPS.gov. We just mark the box." },
  { id: "card", title: "Credit or debit card", sub: "Pay at IRS.gov/PayByCard (a processor fee applies)." },
];

const PayStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const t = useTotals();
  // Nothing's owed - the IRS payment-method indicators (EFTPS/card checkbox, EFW's
  // IRSPayment record) are only required by F2290-068-01 when BalanceDueAmt is
  // non-zero (Return2290Assembler.buildData/isEfwWithBalance gate on the same
  // condition), so there's no IRS-facing reason to make the filer pick one.
  const zeroBalance = t.line6 <= 0;
  const [saving, setSaving] = useState(false);
  const [routingError, setRoutingError] = useState(false);
  const [accountError, setAccountError] = useState(false);
  const [accountTypeError, setAccountTypeError] = useState(false);
  const [methods, setMethods] = useState(DEFAULT_METHODS);
  const [methodsLoading, setMethodsLoading] = useState(true);

  // Payment methods come from the lookup_payment_methods table (the three
  // e-file-representable i2290 "How To Pay the Tax" methods) instead of
  // being hardcoded here - same pattern as FormTypeStep's getFormTypes().
  useEffect(() => {
    let cancelled = false;
    getPaymentMethods()
      .then((list) => {
        if (cancelled) return;
        if (Array.isArray(list) && list.length) {
          setMethods(list.map((m) => ({ id: m.code, title: m.title, sub: m.sub })));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setMethodsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ensures an accurate balance due (t.line6) even on a direct refresh of
  // this step - state.returnTotals isn't persisted across a reload, only
  // returnId is (see AppContext.jsx).
  useEffect(() => {
    if (!getToken() || !state.businessId || state.returnTotals) return;
    let cancelled = false;
    getReturnForBusiness(state.businessId)
      .then((r) => {
        if (!cancelled) dispatch({ type: "SET_RETURN", ...r });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.businessId]);

  // Reloads a previously saved payment choice, same refresh-safe pattern as
  // the other steps. A 404 (nothing saved yet) is expected and ignored.
  useEffect(() => {
    if (!getToken() || !state.returnId) return;
    let cancelled = false;
    getPayment(state.returnId)
      .then((pay) => {
        if (!cancelled) dispatch({ type: "SET_PAY", patch: pay });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.returnId]);

  const selectMethod = (id) => dispatch({ type: "SET_PAY", patch: { method: id } });

  const applyFieldErrors = (err) => {
    const fieldErrors = err?.body?.error?.errors;
    if (!Array.isArray(fieldErrors)) return;
    if (fieldErrors.some((e) => e.field === "bankRouting")) setRoutingError(true);
    if (fieldErrors.some((e) => e.field === "bankAccount")) setAccountError(true);
    if (fieldErrors.some((e) => e.field === "accountType")) setAccountTypeError(true);
  };

  const handleContinue = async () => {
    // Nothing's owed - skip the method requirement entirely and don't save anything
    // (see the zeroBalance definition above for why the choice is XML-inert here).
    if (!zeroBalance) {
      if (!state.pay.method) {
        showToast("Choose how you'll pay the tax.");
        return;
      }

      if (state.pay.method === "efw") {
        const missingAccount = !state.pay.account.trim();
        setRoutingError(!state.pay.routing.trim());
        setAccountError(missingAccount);
        setAccountTypeError(!state.pay.accountType);
        if (!state.pay.routing.trim() || missingAccount || !state.pay.accountType) {
          showToast("Fill in your bank account details.");
          return;
        }
      }

      if (getToken() && state.returnId) {
        setSaving(true);
        try {
          const saved = await savePayment(state.returnId, state.pay);
          dispatch({ type: "SET_PAY", patch: saved });
        } catch (err) {
          setSaving(false);
          applyFieldErrors(err);
          showToast(err.message || "Couldn't save your payment method. Please try again.");
          return;
        }
        setSaving(false);
      }
    }

    onNext();
  };

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Payment method
      </div>
      <h2 className="text-2xl m-[4px_0]">How will you pay the tax?</h2>

      {zeroBalance ? (
        <>
          <p className="text-[var(--muted)] max-w-[62ch] mb-4">
            Nothing's due to the IRS on this return (<b className="font-mono">{money(t.line6)}</b>).
          </p>
          <Banner tone="info" icon="✅">
            No IRS payment method is needed - EFTPS/card/direct-debit only apply when there's a balance due
            (Business Rule F2290-068-01). You can continue to the next step.
          </Banner>
        </>
      ) : (
        <>
          <p className="text-[var(--muted)] max-w-[62ch] mb-4">
            This is the tax owed to the IRS (<b className="font-mono">{money(t.line6)}</b>). Pick one of the three IRS
            methods.
          </p>

          <div className="grid gap-3">
            {methods.map((m) => {
              const sel = state.pay.method === m.id;
              return (
                <div
                  key={m.id}
                  onClick={() => !methodsLoading && selectMethod(m.id)}
                  className={[
                    "flex gap-3 items-start border rounded-[var(--r-sm)] p-3.5 cursor-pointer",
                    sel ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--line-2)]",
                    methodsLoading ? "opacity-60 pointer-events-none" : "",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "w-[18px] h-[18px] rounded-full border-2 flex-none mt-0.5",
                      sel ? "border-[var(--accent)]" : "border-[var(--line-2)]",
                    ].join(" ")}
                    style={
                      sel
                        ? { background: "radial-gradient(circle, var(--accent) 0 5px, transparent 6px)" }
                        : undefined
                    }
                  />
                  <span>
                    <b className="font-semibold">{m.title}</b>
                    <small className="block text-[var(--muted)] text-[12.5px]">{m.sub}</small>
                  </span>
                </div>
              );
            })}
          </div>

          {state.pay.method === "efw" && (
            <div className="mt-3.5">
              <div className="grid gap-3.5 sm:grid-cols-2 mt-1.5">
                <FormField label="Routing number" required error="Enter your 9-digit routing number." showError={routingError}>
                  <TextInput
                    mono
                    placeholder="9 digits"
                    maxLength={9}
                    value={state.pay.routing}
                    onChange={(e) => {
                      dispatch({ type: "SET_PAY", patch: { routing: e.target.value.replace(/\D/g, "").slice(0, 9) } });
                      setRoutingError(false);
                    }}
                    invalid={routingError}
                    disabled={saving}
                  />
                </FormField>
                <FormField
                  label="Account number"
                  required
                  error="Enter your account number."
                  showError={accountError}
                >
                  <TextInput
                    mono
                    placeholder="Account number"
                    maxLength={17}
                    value={state.pay.account}
                    onChange={(e) => {
                      // Digits only - the IRS's own BankAccountNumberType (Common/efileTypes.xsd)
                      // technically allows letters/hyphens too, but no real US bank account number
                      // looks like that; matches PaymentServiceImpl.validateAndNormalize's rejection
                      // of anything non-numeric here (2026-08-13, user-directed product decision).
                      dispatch({ type: "SET_PAY", patch: { account: e.target.value.replace(/\D/g, "").slice(0, 17) } });
                      setAccountError(false);
                    }}
                    invalid={accountError}
                    disabled={saving}
                  />
                </FormField>
              </div>
              <FormField
                label="Account type"
                required
                error="Choose checking or savings."
                showError={accountTypeError}
                style={{ maxWidth: 220 }}
              >
                <Select
                  value={state.pay.accountType}
                  onChange={(e) => {
                    dispatch({ type: "SET_PAY", patch: { accountType: e.target.value } });
                    setAccountTypeError(false);
                  }}
                  invalid={accountTypeError}
                  disabled={saving}
                >
                  <option value="">Select account type…</option>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </Select>
              </FormField>
              <div className="text-xs text-[var(--muted)]">
                Payment date is set to the IRS received date automatically — it can't be a future date you pick.
              </div>
            </div>
          )}
        </>
      )}

      <Banner tone="info" icon="🧾" className="mt-[18px]">
        A separate EasyFile2290 e-filing service fee is collected next.
      </Banner>

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button disabled={(!zeroBalance && !state.pay.method) || saving} onClick={handleContinue}>
          {saving ? "Saving…" : "Continue to payment details →"}
        </Button>
      </div>
    </Panel>
  );
};

export default PayStep;
